import { useId } from 'react';
import { useTranslation } from '@/i18n';

/**
 * What TouchTab.Feedback stores, and what it does with anything longer.
 *
 * `TransactionAnswer.TextAnswer` is `nvarchar(2000)`, and `TransactionService` TRUNCATES past it rather than
 * refusing - deliberately, so one long comment cannot cost a guest their ratings too. That is the right call
 * server-side and the wrong thing to let a guest walk into: they would send a comment, be thanked for it, and
 * never learn that the end of it was thrown away. So the box stops them at the same number the column holds.
 */
export const MAX_COMMENT_LENGTH = 2000;

/** Close enough to the cap to be worth warning about. Below this the counter is noise. */
const COUNTER_APPEARS_WITHIN = 100;

interface FreeTextAnswerProps {
    /** The question, as the restaurant authored it. Becomes the field's label. */
    question: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

/**
 * One question, answered in the guest's own words.
 *
 * The second question type, beside `RatingScale`. A rating says how much; this says why, and it is the only
 * place a restaurant hears something it did not already think to ask about.
 *
 * A real `<textarea>` rather than a growing contenteditable: it gets the phone keyboard's return key, native
 * autocorrect and dictation, and scrolls its own overflow without the page jumping. Four rows is enough to
 * show a sentence or two while leaving the Send button reachable on a phone - with up to five questions on
 * the card, a box tall enough for an essay pushes the button off a screen the guest has already scrolled.
 *
 * Optional, like every question here. Empty is a perfectly good answer and the panel says so - it neither
 * blocks sending nor counts as having answered.
 */
export function FreeTextAnswer({ question, value, onChange, disabled }: FreeTextAnswerProps) {
    const { t } = useTranslation();
    const fieldId = useId();
    const counterId = `${fieldId}-counter`;

    const remaining = MAX_COMMENT_LENGTH - value.length;
    const showCounter = remaining <= COUNTER_APPEARS_WITHIN;

    return (
        <div className="flex min-w-0 flex-col">
            <label htmlFor={fieldId} className="mb-2 text-[0.9375rem] font-medium text-pretty">
                {question}
            </label>

            <textarea
                id={fieldId}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                disabled={disabled}
                rows={4}
                maxLength={MAX_COMMENT_LENGTH}
                placeholder={t('feedback.comment.placeholder')}
                aria-describedby={showCounter ? counterId : undefined}
                className={[
                    'w-full rounded-xl border border-border bg-background px-3 py-2.5',
                    'text-[0.9375rem] text-foreground placeholder:text-muted-foreground',
                    // Matches the focus treatment on the rating faces, so the two question types look like
                    // one form rather than two components that happen to be stacked.
                    'focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2',
                    'focus-visible:outline-foreground',
                    'disabled:opacity-60',
                ].join(' ')}
            />

            {showCounter && (
                /*
                 * `polite` and only near the limit. Announcing a countdown on every keystroke would talk over
                 * the guest for the whole 2000 characters; announcing nothing would let them hit the cap with
                 * no idea why the keyboard stopped responding.
                 */
                <p id={counterId} aria-live="polite" className="mt-1.5 text-sm text-muted-foreground">
                    {t('feedback.comment.remaining', { count: remaining })}
                </p>
            )}
        </div>
    );
}
